import { useRef, useCallback, useState, useEffect } from 'react';
import useDetectionStore from '../store/useDetectionStore';

const MAX_HISTORY = 50;

/**
 * Undo/Redo for DetectionLabel canvas (annotations + polygons).
 * Saves state before each modification; Ctrl+Z undoes, Ctrl+Y/Ctrl+Shift+Z redoes.
 */
const useUndoRedo = () => {
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const isRestoringRef = useRef(false);

  const {
    getCurrentImage,
    restoreAnnotationsAndPolygons,
    currentImageIndex,
  } = useDetectionStore();

  const updateCanUndoRedo = useCallback(() => {
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  // Clear stacks when switching images
  useEffect(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    updateCanUndoRedo();
  }, [currentImageIndex, updateCanUndoRedo]);

  const saveState = useCallback(() => {
    if (isRestoringRef.current) return;
    const currentImage = getCurrentImage();
    if (!currentImage) return;

    const state = {
      annotations: JSON.parse(JSON.stringify(currentImage.annotations || [])),
      polygons: JSON.parse(JSON.stringify(currentImage.polygons || [])),
    };

    undoStackRef.current.push(state);
    if (undoStackRef.current.length > MAX_HISTORY) {
      undoStackRef.current.shift();
    }
    redoStackRef.current = [];
    updateCanUndoRedo();
  }, [getCurrentImage, updateCanUndoRedo]);

  const undo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;

    const currentImage = getCurrentImage();
    if (!currentImage) return;

    // Push current state to redo before restoring
    redoStackRef.current.push({
      annotations: JSON.parse(JSON.stringify(currentImage.annotations || [])),
      polygons: JSON.parse(JSON.stringify(currentImage.polygons || [])),
    });

    const prevState = undoStackRef.current.pop();
    isRestoringRef.current = true;
    restoreAnnotationsAndPolygons(prevState.annotations, prevState.polygons);
    isRestoringRef.current = false;

    updateCanUndoRedo();
  }, [getCurrentImage, restoreAnnotationsAndPolygons, updateCanUndoRedo]);

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;

    const currentImage = getCurrentImage();
    if (!currentImage) return;

    // Push current state to undo before restoring
    undoStackRef.current.push({
      annotations: JSON.parse(JSON.stringify(currentImage.annotations || [])),
      polygons: JSON.parse(JSON.stringify(currentImage.polygons || [])),
    });

    const nextState = redoStackRef.current.pop();
    isRestoringRef.current = true;
    restoreAnnotationsAndPolygons(nextState.annotations, nextState.polygons);
    isRestoringRef.current = false;

    updateCanUndoRedo();
  }, [getCurrentImage, restoreAnnotationsAndPolygons, updateCanUndoRedo]);

  return { saveState, undo, redo, canUndo, canRedo };
};

export default useUndoRedo;
